import type { ActivityContext, ActivityHandle } from '../../core/activity';
import levelsData from './levels.json';

interface DataEntry {
  subLevels: number;
  arrows: number;
  circleValues: number[];
}

interface Level {
  level: number;
  data: DataEntry[];
}

interface LevelsFile {
  levels: Level[];
}

const LEVELS = (levelsData as LevelsFile).levels;
const RING_COLORS = ['#ef4444', '#fbbf24', '#3b82f6', '#10b981', '#a855f7', '#ec4899', '#f97316', '#06b6d4', '#84cc16', '#dc2626', '#facc15', '#6366f1', '#14b8a6', '#d946ef'];

interface Hit {
  ringIdx: number;
  value: number;
  // posição dentro do SVG do alvo (viewBox 0–200): assim a flecha fica grudada no alvo
  // mesmo enquanto o alvo continua deslizando.
  svgX: number;
  svgY: number;
}

function slideDurationFor(levelNumber: number): number {
  if (levelNumber <= 1) return 7;
  if (levelNumber <= 3) return 5;
  return 3.5;
}

export function mountTarget(ctx: ActivityContext): ActivityHandle {
  const level = LEVELS.find((l) => l.level === ctx.level) ?? LEVELS[0];
  if (!level) {
    ctx.root.innerHTML = '<p>Nível não encontrado.</p>';
    return { unmount: () => { ctx.root.innerHTML = ''; } };
  }

  const totalSubs = level.data.reduce((acc, d) => acc + d.subLevels, 0);
  let dataIdx = 0;
  let subWithinData = 0;
  let correctCount = 0;
  let arrowsLeft = 0;
  let hits: Hit[] = [];
  let buttonsBlocked = false;
  // Mantemos a referência ao SVG do alvo pra não destruí-lo (e reiniciar a animação)
  // a cada flecha disparada. Recriamos só ao trocar de sub-nível.
  let targetSvg: SVGSVGElement | null = null;

  const page = document.createElement('div');
  page.className = 'activity-page';
  page.innerHTML = `
    <header class="activity-bar">
      <button class="activity-bar__back" type="button" aria-label="Voltar ao hub">← Voltar</button>
      <div class="activity-bar__title">Arco e flecha</div>
      <div class="activity-bar__level">Nível ${level.level} de ${LEVELS.length}</div>
    </header>
    <section class="activity-stage tgt">
      <div class="feedback" aria-live="polite"></div>
      <p class="tgt__progress" aria-live="polite"></p>
      <p class="tgt__hint">Clique no alvo pra atirar a flecha! O alvo se move.</p>
      <div class="tgt__status" aria-live="polite"></div>
      <div class="tgt__field-wrap"><div class="tgt__field"></div></div>
      <div class="tgt__hits-list"></div>
      <div class="tgt__answer" style="display:none">
        <div class="tgt__answer-prompt"></div>
        <div class="tgt__answer-input-row">
          <input type="number" inputmode="numeric" class="tgt__answer-input" />
          <button class="btn btn--primary tgt__answer-btn" type="button">Conferir</button>
        </div>
      </div>
    </section>
  `;
  ctx.root.appendChild(page);

  const progressEl = page.querySelector<HTMLElement>('.tgt__progress')!;
  const statusEl = page.querySelector<HTMLElement>('.tgt__status')!;
  const fieldEl = page.querySelector<HTMLElement>('.tgt__field')!;
  const hitsListEl = page.querySelector<HTMLElement>('.tgt__hits-list')!;
  const answerEl = page.querySelector<HTMLElement>('.tgt__answer')!;
  const answerPromptEl = page.querySelector<HTMLElement>('.tgt__answer-prompt')!;
  const answerInput = page.querySelector<HTMLInputElement>('.tgt__answer-input')!;
  const answerBtn = page.querySelector<HTMLButtonElement>('.tgt__answer-btn')!;
  const feedback = page.querySelector<HTMLElement>('.feedback')!;
  page.querySelector<HTMLElement>('.activity-bar__back')!.addEventListener('click', () => ctx.onExit());

  answerBtn.addEventListener('click', validateAnswer);
  answerInput.addEventListener('keydown', (e) => { if (e.key === 'Enter') { e.preventDefault(); validateAnswer(); } });

  fieldEl.addEventListener('click', onFieldClick);

  function startSubLevel(): void {
    const cfg = level.data[dataIdx]!;
    arrowsLeft = cfg.arrows;
    hits = [];
    buttonsBlocked = false;
    answerInput.value = '';
    answerEl.style.display = 'none';
    // Força recriação do alvo na nova rodada
    targetSvg = null;
    fieldEl.innerHTML = '';
    renderAll();
  }

  function currentConfig(): DataEntry {
    return level.data[dataIdx]!;
  }

  function renderAll(): void {
    renderProgress();
    renderStatus();
    ensureTarget();
    renderHits();
  }

  function renderProgress(): void {
    const overall = level.data.slice(0, dataIdx).reduce((acc, d) => acc + d.subLevels, 0) + subWithinData;
    progressEl.textContent = `Rodada ${overall + 1} de ${totalSubs}`;
  }

  function renderStatus(): void {
    const cfg = currentConfig();
    const score = hits.reduce((a, h) => a + h.value, 0);
    statusEl.innerHTML = `<span>🏹 Flechas: ${arrowsLeft}/${cfg.arrows}</span><span>⭐ Pontos: ${score}</span>`;
  }

  function ensureTarget(): void {
    if (targetSvg && fieldEl.contains(targetSvg)) {
      // Só atualiza a classe --moving (pra parar a animação quando acabar as flechas)
      const moving = arrowsLeft > 0;
      targetSvg.classList.toggle('tgt__target--moving', moving);
      return;
    }
    fieldEl.innerHTML = '';
    const cfg = currentConfig();
    const slideDur = slideDurationFor(level.level);
    const target = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    target.setAttribute('viewBox', '0 0 200 200');
    target.setAttribute('class', `tgt__target ${arrowsLeft > 0 ? 'tgt__target--moving' : ''}`);
    target.style.setProperty('--slide-duration', `${slideDur}s`);
    const total = cfg.circleValues.length;
    cfg.circleValues.forEach((_, i) => {
      const value = cfg.circleValues[total - 1 - i]!;
      const r = 100 - (i * (90 / total));
      const circle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
      circle.setAttribute('cx', '100');
      circle.setAttribute('cy', '100');
      circle.setAttribute('r', String(r));
      circle.setAttribute('class', 'tgt__ring');
      circle.style.setProperty('--ring-color', RING_COLORS[i % RING_COLORS.length] ?? '#ef4444');
      target.appendChild(circle);
      const text = document.createElementNS('http://www.w3.org/2000/svg', 'text');
      text.setAttribute('x', '100');
      text.setAttribute('y', String(100 - r + 8));
      text.setAttribute('class', 'tgt__ring-label');
      text.textContent = String(value);
      target.appendChild(text);
    });
    // Grupo pras flechas — fica DENTRO do SVG, então se move com o alvo
    const arrowGroup = document.createElementNS('http://www.w3.org/2000/svg', 'g');
    arrowGroup.setAttribute('class', 'tgt__arrow-group');
    target.appendChild(arrowGroup);
    fieldEl.appendChild(target);
    targetSvg = target;
  }

  function renderHits(): void {
    if (!targetSvg) return;
    const group = targetSvg.querySelector('.tgt__arrow-group');
    if (!group) return;
    group.innerHTML = '';
    for (const h of hits) {
      const text = document.createElementNS('http://www.w3.org/2000/svg', 'text');
      text.setAttribute('x', String(h.svgX));
      text.setAttribute('y', String(h.svgY));
      text.setAttribute('class', 'tgt__hit-svg');
      text.textContent = '🎯';
      group.appendChild(text);
    }
    // Lista de chips (+4 +2 +4) abaixo do campo
    hitsListEl.innerHTML = '';
    for (const h of hits) {
      const chip = document.createElement('span');
      chip.className = 'tgt__hit-chip';
      chip.textContent = `+${h.value}`;
      hitsListEl.appendChild(chip);
    }
  }

  function onFieldClick(e: MouseEvent): void {
    if (buttonsBlocked) return;
    if (arrowsLeft <= 0) return;
    if (!targetSvg) return;
    // Converte o click pra coordenadas do viewBox do SVG.
    // Assim o ponto fica fixo no SVG mesmo enquanto ele desliza.
    const pt = targetSvg.createSVGPoint();
    pt.x = e.clientX;
    pt.y = e.clientY;
    const ctm = targetSvg.getScreenCTM();
    if (!ctm) return;
    const svgPt = pt.matrixTransform(ctm.inverse());
    const svgX = svgPt.x;
    const svgY = svgPt.y;
    // Centro do alvo no viewBox = (100, 100)
    const dx = svgX - 100;
    const dy = svgY - 100;
    const dist = Math.sqrt(dx * dx + dy * dy);
    if (dist > 100) {
      // Errou alvo: ignora click (mantém flechas)
      return;
    }
    const cfg = currentConfig();
    const total = cfg.circleValues.length;
    let ringIdx = -1;
    for (let i = 0; i < total; i++) {
      const rOut = 100 - i * (90 / total);
      const rIn = 100 - (i + 1) * (90 / total);
      if (dist <= rOut && dist > rIn) {
        ringIdx = i;
        break;
      }
      if (i === total - 1 && dist <= rOut) {
        ringIdx = i;
        break;
      }
    }
    if (ringIdx === -1) ringIdx = total - 1;
    const value = cfg.circleValues[total - 1 - ringIdx]!;
    ctx.audio.play('click');
    arrowsLeft--;
    hits.push({ ringIdx, value, svgX, svgY });
    renderAll();
    checkAllArrowsUsed();
  }

  function checkAllArrowsUsed(): void {
    if (arrowsLeft > 0) return;
    // Mostra input pro aluno digitar a soma
    buttonsBlocked = true;
    const score = hits.reduce((a, h) => a + h.value, 0);
    const cfg = currentConfig();
    answerPromptEl.textContent = `Você atirou ${cfg.arrows} flechas. Qual a soma total dos pontos?`;
    answerEl.style.display = 'flex';
    answerInput.value = '';
    answerInput.focus();
    // Salva score como atributo pra validar depois
    answerEl.dataset['score'] = String(score);
  }

  function validateAnswer(): void {
    const expected = parseInt(answerEl.dataset['score'] ?? '0', 10);
    const given = parseInt(answerInput.value, 10);
    if (!Number.isFinite(given)) {
      ctx.audio.play('wrong');
      return;
    }
    if (given === expected) {
      ctx.audio.play('correct');
      showFeedback('ok');
      correctCount++;
      window.setTimeout(() => advance(), 900);
    } else {
      ctx.audio.play('wrong');
      showFeedback('err');
      answerPromptEl.textContent = `Quase! A soma correta era ${expected}. Vamos pra próxima!`;
      window.setTimeout(() => advance(), 1500);
    }
  }

  function advance(): void {
    subWithinData++;
    if (subWithinData >= currentConfig().subLevels) {
      subWithinData = 0;
      dataIdx++;
    }
    if (dataIdx >= level.data.length) finishLevel();
    else startSubLevel();
  }

  function showFeedback(kind: 'ok' | 'err'): void {
    feedback.textContent = kind === 'ok' ? '✓' : '✗';
    feedback.className = `feedback feedback--show feedback--${kind}`;
    window.setTimeout(() => feedback.classList.remove('feedback--show'), 500);
  }

  function finishLevel(): void {
    ctx.audio.play('win');
    ctx.onComplete(level.level, correctCount);
    const stage = page.querySelector<HTMLElement>('.activity-stage')!;
    stage.innerHTML = `
      <h2 class="activity-finish__title">Nível ${level.level} concluído!</h2>
      <p class="activity-finish__text">Você acertou ${correctCount} de ${totalSubs} somas.</p>
      <div class="activity-finish__actions">
        <button class="btn btn--secondary" type="button" data-act="hub">Voltar ao hub</button>
        ${level.level < LEVELS.length
          ? `<button class="btn btn--primary" type="button" data-act="next">Próximo nível</button>`
          : ''}
      </div>
    `;
    stage.querySelector<HTMLElement>('[data-act="hub"]')!.addEventListener('click', () => ctx.onExit());
    const next = stage.querySelector<HTMLElement>('[data-act="next"]');
    if (next) {
      next.addEventListener('click', () => {
        window.location.hash = `/target/${level.level + 1}`;
      });
    }
  }

  startSubLevel();

  return {
    unmount(): void {
      ctx.root.innerHTML = '';
    },
  };
}
